import { useEffect } from "react";
import { Dialog, DialogContent, Box, Typography, Stack, IconButton, TextField } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { FoodsIcon, Button as FoodsButton } from "@foodpilot/foods";
import { IngredientLine } from "src/api/client-api/ingredientLines/useGetAllIngredientLine";
import {
    IngredientLineWrite,
    useCreateIngredientLine,
} from "src/api/client-api/ingredientLines/useCreateIngredientLine";
import { useUpdateIngredientLine } from "src/api/client-api/ingredientLines/useUpdateIngredientLine";
import { useSnackbar } from "src/utils/useSnackbar";

// Schema for ingredient line form
const IngredientLineFormSchema = z.object({
    name: z.string().min(1, "Ingredient line name is required"),
});

type IngredientLineFormData = z.infer<typeof IngredientLineFormSchema>;

type IngredientLineModalProps = {
    open: boolean;
    onClose: () => void;
    ingredientLine?: IngredientLine;
    onSuccess?: (ingredientLine: any) => void;
};

export const IngredientLineModal = (props: IngredientLineModalProps) => {
    const { open, onClose, ingredientLine, onSuccess } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const createIngredientLine = useCreateIngredientLine();
    const updateIngredientLine = useUpdateIngredientLine();

    const {
        control,
        handleSubmit,
        formState: { errors, isValid },
        reset,
    } = useForm<IngredientLineFormData>({
        mode: "onChange",
        resolver: zodResolver(IngredientLineFormSchema),
    });

    const onSubmitHandler: SubmitHandler<IngredientLineFormData> = (values) => {
        const ingredientLineWrite: IngredientLineWrite = {
            name: values.name,
        };

        if (ingredientLine) {
            // Update existing ingredient line
            updateIngredientLine.mutate(
                { ingredientLineId: ingredientLine.id, ingredientLine: ingredientLineWrite },
                {
                    onSuccess: (response) => {
                        const savedIngredientLine = response.data;
                        if (onSuccess) {
                            onSuccess(savedIngredientLine);
                        }
                        handleClose();
                    },
                    onError: (error) => {
                        snackbar.setSnackbarProps({
                            message: t(
                                "ingredientLine.error.updateFailed",
                                "Failed to update ingredient line. Please try again.",
                            ),
                            type: "error",
                        });
                    },
                },
            );
        } else {
            // Create new ingredient line
            createIngredientLine.mutate(
                { ingredientLine: ingredientLineWrite },
                {
                    onSuccess: (response) => {
                        const savedIngredientLine = response.data;
                        if (onSuccess) {
                            onSuccess(savedIngredientLine);
                        }
                        handleClose();
                    },
                    onError: (error) => {
                        snackbar.setSnackbarProps({
                            message: t(
                                "ingredientLine.error.saveFailed",
                                "Failed to save ingredient line. Please try again.",
                            ),
                            type: "error",
                        });
                    },
                },
            );
        }
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            const formData: IngredientLineFormData = {
                name: ingredientLine?.name || "",
            };
            reset(formData);
        }
    }, [open, ingredientLine, reset]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    maxHeight: "90vh",
                    width: "638px",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px 32px 0px 32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E" }}>
                        {ingredientLine ?
                            t("edit_ingredient_line", "Edit ingredient line")
                        :   t("add_ingredient_line", "Add an ingredient line")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        padding: "8px",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                    }}
                >
                    <FoodsIcon icon="close" size={1.5} />
                </IconButton>
            </Box>

            {/* Content */}
            <DialogContent
                sx={{
                    padding: "32px",
                    backgroundColor: "#FEFCFB",
                    overflowY: "auto",
                }}
            >
                <form onSubmit={handleSubmit(onSubmitHandler)}>
                    <Stack spacing="24px">
                        {/* Information Section */}
                        <Box>
                            <Typography
                                variant="h4"
                                sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "32px" }}
                            >
                                {t("information", "Information")}
                            </Typography>
                            <Stack spacing="24px">
                                {/* Ingredient Line name */}
                                <Box sx={{ width: "100%" }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}
                                    >
                                        {t("nom_de_la_filiere", "Ingredient line name")}* :
                                    </Typography>
                                    <Controller
                                        name="name"
                                        control={control}
                                        render={({ field: { ref, ...field } }) => (
                                            <TextField
                                                {...field}
                                                inputRef={ref}
                                                placeholder={t(
                                                    "enter_ingredient_line_name",
                                                    "Enter ingredient line name",
                                                )}
                                                variant="outlined"
                                                error={Boolean(errors.name)}
                                                helperText={errors.name?.message}
                                                sx={{
                                                    width: "100%",
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "24px",
                                                        backgroundColor: "#FFFFFF",
                                                        border: "1px solid #E7E6E4",
                                                        height: "40px",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                        "&:hover fieldset": {
                                                            border: "none",
                                                        },
                                                        "&.Mui-focused fieldset": {
                                                            border: "none",
                                                        },
                                                    },
                                                    "& .MuiInputBase-input": {
                                                        fontSize: "0.875rem",
                                                        fontWeight: 500,
                                                        color: "#393A36",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: "16px",
                                justifyContent: "flex-end",
                                marginTop: "32px",
                            }}
                        >
                            <FoodsButton
                                variant="secondary"
                                onClick={handleClose}
                                sx={{
                                    borderRadius: "44px",
                                    px: 2.5,
                                    py: 1.25,
                                    height: "40px",
                                    fontSize: "0.875rem",
                                    fontWeight: 700,
                                    border: "1px solid #E7E6E4",
                                    color: "#393A36",
                                    backgroundColor: "white",
                                    "&:hover": {
                                        backgroundColor: "#F5F5F5",
                                    },
                                }}
                            >
                                {t("cancel", "Cancel")}
                            </FoodsButton>
                            <FoodsButton
                                type="submit"
                                variant="primary"
                                disabled={!isValid || createIngredientLine.isLoading || updateIngredientLine.isLoading}
                                sx={(theme) => ({
                                    borderRadius: "44px",
                                    padding: "0px 20px",
                                    height: "40px",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    color: "white !important",
                                    boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                    "&:hover": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                        boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                        color: "white !important",
                                    },
                                    "&:disabled": {
                                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                        opacity: 0.36,
                                        color: "white !important",
                                    },
                                })}
                            >
                                {createIngredientLine.isLoading || updateIngredientLine.isLoading ?
                                    t("saving", "Saving...")
                                : ingredientLine ?
                                    t("update", "Update")
                                :   t("save", "Save")}
                            </FoodsButton>
                        </Box>
                    </Stack>
                </form>
            </DialogContent>
        </Dialog>
    );
};
